import { get, post, patch, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'PartnerPortalAPI';

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: token } : {};
}

async function apiGet(path) {
  const headers = await authHeaders();
  const op = get({ apiName: API_NAME, path, options: { headers } });
  const { body } = await op.response;
  return body.json();
}

async function apiPost(path, data) {
  const headers = await authHeaders();
  const op = post({ apiName: API_NAME, path, options: { body: data, headers } });
  const { body } = await op.response;
  return body.json();
}

async function apiPatch(path, data) {
  const headers = await authHeaders();
  const op = patch({ apiName: API_NAME, path, options: { body: data, headers } });
  const { body } = await op.response;
  return body.json();
}

// ── Opportunities ────────────────────────────────────────────────────────────

export const opportunityService = {
  create: (data) => apiPost('/opportunities', data),
  getAll: () => apiGet('/opportunities'),
  getById: (id) => apiGet(`/opportunities/${id}`),
  update: (id, data) => apiPatch(`/opportunities/${id}`, data),
  getAudit: (id) => apiGet(`/opportunities/${id}/audit`),
  partnerApprove: (id) => apiPost(`/opportunities/${id}/partner-approve`, {}),
  partnerReject: (id, reason) => apiPost(`/opportunities/${id}/partner-reject`, { rejection_reason: reason }),
};

// ── Partner Users (managers invite salespeople) ──────────────────────────────

export const partnerUserService = {
  invite: (data) => apiPost('/partner-users', data),
  listCompanyUsers: () => apiGet('/partner-users'),
};

// ── Meetings ─────────────────────────────────────────────────────────────────

export const meetingService = {
  create: (opportunityId, data) => apiPost(`/opportunities/${opportunityId}/meetings`, data),
  getAll: (opportunityId) => apiGet(`/opportunities/${opportunityId}/meetings`),
  update: (opportunityId, meetingId, data) => apiPatch(`/opportunities/${opportunityId}/meetings/${meetingId}`, data),
};

// ── Comments ─────────────────────────────────────────────────────────────────

export const commentService = {
  getAll: (opportunityId) => apiGet(`/opportunities/${opportunityId}/comments`),
  add: (opportunityId, text, type = 'comment') =>
    apiPost(`/opportunities/${opportunityId}/comments`, { text, type }),
};

// ── Documents ─────────────────────────────────────────────────────────────────

export const documentService = {
  list: (opportunityId) => apiGet(`/opportunities/${opportunityId}/documents`),
  requestUploadUrl: (opportunityId, file_name, content_type, label = '') =>
    apiPost(`/opportunities/${opportunityId}/documents`, { file_name, content_type, label }),
  async uploadToS3(uploadUrl, file) {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!res.ok) throw new Error('Upload failed');
  },
};

// ── Public (unauthenticated) ──────────────────────────────────────────────────

export const publicService = {
  async submitAccessRequest(data) {
    const endpoint = process.env.REACT_APP_API_ENDPOINT?.replace(/\/$/, '');
    const res = await fetch(`${endpoint}/access-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Request failed');
    return res.json();
  }
};

// ── Admin ─────────────────────────────────────────────────────────────────────

export const adminService = {
  getAllOpportunities(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return apiGet(`/admin/opportunities${qs ? '?' + qs : ''}`);
  },
  updateOpportunity: (id, data) => apiPatch(`/admin/opportunities/${id}`, data),
  getAnalytics: () => apiGet('/admin/analytics'),
  async exportOpportunities() {
    const headers = await authHeaders();
    const op = get({ apiName: API_NAME, path: '/admin/export', options: { headers } });
    const { body } = await op.response;
    return body.text();
  },
  listAccessRequests: () => apiGet('/admin/access-requests'),
  inviteUser: (userData) => apiPost('/admin/users', userData),
  listUsers: () => apiGet('/admin/users'),
  async deleteUser(email) {
    const headers = await authHeaders();
    const op = del({ apiName: API_NAME, path: `/admin/users/${encodeURIComponent(email)}`, options: { headers } });
    const { body } = await op.response;
    return body.json();
  },
};
